/** Human labels and small derivations for sessions. Pure, so they are unit tested. */
import { agentKindOf, failedOnModel, resumeArgv } from '@orgus/protocol';
import type { Session, SessionStatus } from '@orgus/protocol';

const FAMILY = /claude-(opus|sonnet|haiku)-(\d+)(?:[-.](\d{1,2}))?(?![\d])/i;
const LEGACY = /claude-(\d+)(?:[-.](\d))?-(opus|sonnet|haiku)/i;
const GPT = /^gpt-(\d+(?:\.\d+)?)(?:-(codex|mini|nano))?(?:-(mini|max))?$/i;

/** 'claude-opus-5' gives 'Opus 5'; 'us.anthropic.claude-sonnet-4-5-20250929-v1:0' gives 'Sonnet 4.5'; 'gpt-5-codex' gives 'GPT-5 Codex'. Unknown ids pass through. */
export function modelLabel(model: string | null | undefined): string {
  if (!model) return 'Default';
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  const m = FAMILY.exec(model);
  if (m) return `${cap(m[1]!)} ${m[2]}${m[3] ? `.${m[3]}` : ''}`;
  const l = LEGACY.exec(model);
  if (l) return `${cap(l[3]!)} ${l[1]}${l[2] ? `.${l[2]}` : ''}`;
  const g = GPT.exec(model);
  if (g) return [`GPT-${g[1]}`, g[2] && cap(g[2]), g[3] && cap(g[3])].filter(Boolean).join(' ');
  return model;
}

/**
 * The Claude Code CLI's model aliases: each resolves to the newest model of its family the
 * account can use, so they never fail with "model not found" the way a pinned id can.
 */
export const MODEL_ALIASES: Record<string, string> = { opus: 'Opus (newest)', sonnet: 'Sonnet (newest)', haiku: 'Haiku (newest)' };

/** Default agent models per kind: Claude Code's aliases, Codex's own ids. */
export const AGENT_DEFAULT_MODELS: Record<string, readonly string[]> = {
  'claude-code': Object.keys(MODEL_ALIASES),
  codex: ['gpt-5-codex', 'gpt-5'],
};

/** A model as a picker shows it: aliases by family, ids by family and version. */
export function modelOptionLabel(model: string): string {
  return MODEL_ALIASES[model] ?? modelLabel(model);
}

type ModelEvidence = Pick<Session, 'model' | 'tokens' | 'lastText'> & Partial<Pick<Session, 'agentKind'>>;

/**
 * Models a session may start with, for one agent kind: the defaults (Claude Code's aliases),
 * then every model a session of that kind actually answered with (it produced output tokens and
 * did not end on a model error). A model that only ever failed is never offered. `keep` stays
 * listed (the model a mission or form already holds).
 */
export function provenModels(sessions: Iterable<ModelEvidence>, kind = 'claude-code', keep?: string | null): string[] {
  const out = [...(AGENT_DEFAULT_MODELS[kind] ?? [])];
  const seen = new Set(out);
  const proven: string[] = [];
  for (const s of sessions) {
    if (!s.model || seen.has(s.model) || agentKindOf(s) !== kind) continue;
    if (s.tokens.output <= 0 || failedOnModel(s)) continue;
    seen.add(s.model);
    proven.push(s.model);
  }
  proven.sort((a, b) => modelOptionLabel(a).localeCompare(modelOptionLabel(b), 'en-US', { numeric: true }));
  if (keep && !seen.has(keep)) proven.push(keep);
  return [...out, ...proven];
}

export const PERMISSION_MODES = ['default', 'acceptEdits', 'plan', 'bypassPermissions'] as const;

const PERMISSION_LABEL: Record<string, string> = {
  default: 'Ask before acting',
  acceptEdits: 'Accept edits',
  plan: 'Plan only',
  bypassPermissions: 'Bypass permissions',
  dontAsk: 'Do not ask',
  // Codex sandbox modes (what a Codex session reports as its permissions).
  'read-only': 'Read only',
  'workspace-write': 'Workspace write',
  'danger-full-access': 'Full access',
};

export function permissionLabel(mode: string | null | undefined): string {
  if (!mode) return 'Ask before acting';
  return PERMISSION_LABEL[mode] ?? mode;
}

/**
 * The permission choice as an agent understands it. Codex has no "ask" for a headless
 * run: its default is Codex's own setting, read-only for codex exec, which never asks.
 */
export function permissionLabelFor(kind: string, mode: string): string {
  if (kind === 'codex' && mode === 'default') return 'Codex default (read only)';
  return permissionLabel(mode);
}

/** Input plus output tokens: what the session produced and read fresh. Cache reads are shown separately. */
export function tokenTotal(s: Pick<Session, 'tokens'>): number {
  return s.tokens.input + s.tokens.output;
}

/** Order inside a group: what needs you first, then live work, then the rest by recency. */
export const STATUS_RANK: Record<SessionStatus, number> = { waiting: 0, working: 1, idle: 2, ended: 3 };

export function compareSessions(a: Session, b: Session): number {
  return STATUS_RANK[a.status] - STATUS_RANK[b.status] || b.lastActivity - a.lastActivity;
}

export function isLive(s: Pick<Session, 'status'>): boolean {
  return s.status !== 'ended';
}

/** The one command that resumes a session in a terminal (claude --resume, codex resume, ...). */
export function resumeCommand(s: Pick<Session, 'id' | 'cwd' | 'agentKind'>, os?: 'windows' | 'macos' | 'linux'): string {
  const cwd = /\s/.test(s.cwd) ? `"${s.cwd}"` : s.cwd;
  const join = os === 'windows' ? '; ' : ' && ';
  const argv = resumeArgv(agentKindOf(s), s.id) ?? ['claude', '--resume', s.id];
  return `cd ${cwd}${join}${argv.join(' ')}`;
}
